/**
 * Wattec — picture of the chosen component in its own picker
 *
 * Easify's picker shows a list of components, each with its photo. Once a
 * customer picks one the list closes and only the component's name is left
 * in the box, so a build of a dozen parts reads as a column of text and
 * there is nothing to recognise a part by at a glance.
 *
 * This puts the chosen component's own photo back into the box it was
 * chosen from, as a small square at the left-hand end of the field.
 *
 * It only ever reads what Easify has already rendered and adds one image of
 * its own beside it. It never changes a value, presses a control, or touches
 * a class Easify's own logic depends on, so pricing, conditional options and
 * add to cart all behave exactly as before. If anything here fails, the
 * picker is left exactly as Easify drew it.
 *
 * Used on every product page that has Easify options — the Custom PC
 * template and the ordinary product template alike.
 */
(function () {
  'use strict';

  var THUMB_CLASS = 'wt-optthumb';
  var HAS_THUMB_CLASS = 'wt-has-thumb';

  /* ---- small helpers ------------------------------------------------ */

  /** Collapses runs of whitespace so two labels can be compared fairly. */
  function flatten(text) {
    return String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
  }

  function directChild(parent, selector) {
    for (var i = 0; i < parent.children.length; i++) {
      if (parent.children[i].matches && parent.children[i].matches(selector)) {
        return parent.children[i];
      }
    }
    return null;
  }

  /** The address an <img> is actually showing, whichever attribute holds it. */
  function sourceOf(img) {
    if (!img) return '';
    var src = img.getAttribute('src') || img.getAttribute('data-src') || '';
    return flatten(src);
  }

  /* ---- finding the component the customer chose --------------------- */

  /**
   * Easify marks the chosen card in the list it keeps in the page, so the
   * photo is already there to read — nothing is fetched and nothing waits
   * on a re-render.
   *
   * Three ways of finding it, in order of how much they can be trusted:
   *   1. Easify's own preview image, on the versions that fill it in;
   *   2. the card Easify has marked as chosen;
   *   3. the card whose name matches the text now in the field.
   */
  function chosenImage(picker, value) {
    var own = directChild(picker, '.tpo_option-input-inner');
    own = own && own.querySelector('img.tpo_dropdown-image-wrapper');
    var ownSrc = sourceOf(own);
    if (ownSrc) return ownSrc;

    var list = picker.querySelector('.tpo_option-type-dropdown-wrapper');
    if (!list) return '';

    var card = list.querySelector(
      '.tpo_option-type-selection.tpo_checked, .tpo_option-type-selection[aria-selected="true"]'
    );

    if (!card) {
      // A card's text is the component's name followed by its spec line and
      // its price, so it is matched on its opening words rather than in
      // full — the same rule the build summary already uses.
      var wanted = flatten(value).toLowerCase();
      var cards = list.querySelectorAll('.tpo_option-type-selection');
      for (var i = 0; i < cards.length && wanted; i++) {
        if (cards[i].classList.contains('tpo_option-type-selection--empty')) continue;
        var text = flatten(cards[i].textContent).toLowerCase();
        if (text && text.indexOf(wanted) === 0) { card = cards[i]; break; }
      }
    }

    return sourceOf(card && card.querySelector('img'));
  }

  /* ---- the image this file adds ------------------------------------- */

  function makeThumb() {
    var img = document.createElement('img');
    img.className = THUMB_CLASS;
    // The component's name is already written in the field right beside it,
    // so to a screen reader this picture is decoration and nothing more.
    img.alt = '';
    img.setAttribute('aria-hidden', 'true');
    img.setAttribute('loading', 'lazy');
    img.setAttribute('decoding', 'async');
    // A photo that will not load leaves no gap and no broken-image icon.
    img.addEventListener('error', function () { clear(img.parentNode); });
    return img;
  }

  function clear(inner) {
    if (!inner) return;
    inner.classList.remove(HAS_THUMB_CLASS);
    var existing = inner.querySelector('.' + THUMB_CLASS);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  /* ---- the chosen component's name, in full ---------------------------
   * The field is a one-line text box, so a long component name followed by
   * its price ("Team 256GB CX2 SSD [ +£39.00 ]") runs off the end of it on a
   * phone. Once a real component is chosen, a copy of the same words is laid
   * over the field where it can wrap — the name on as many lines as it needs,
   * the price on a line of its own — and the stylesheet lets the field grow
   * to hold it. While the picker still reads "Default" nothing is added, so
   * an untouched picker looks exactly as it always has.
   * ------------------------------------------------------------------ */

  var LABEL_CLASS = 'wt-optlabel';
  var HAS_LABEL_CLASS = 'wt-has-label';
  var NOTHING_CHOSEN = /^(default|none|no thanks?|not required)$/i;

  /** Splits Easify's "Name [ +£39.00 ]" into the name and the price. */
  function splitValue(value) {
    var match = value.match(/^(.*?)\s*\[\s*([^\]]+?)\s*\]\s*$/);
    return match ? { name: match[1], price: match[2] } : { name: value, price: '' };
  }

  function clearLabel(inner) {
    inner.classList.remove(HAS_LABEL_CLASS);
    var existing = inner.querySelector('.' + LABEL_CLASS);
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
  }

  function syncLabel(inner, value) {
    var parts = splitValue(value);
    if (!value || NOTHING_CHOSEN.test(parts.name)) { clearLabel(inner); return; }

    var label = inner.querySelector('.' + LABEL_CLASS);
    if (!label) {
      label = document.createElement('span');
      label.className = LABEL_CLASS;
      // The field itself still carries the same words for screen readers.
      label.setAttribute('aria-hidden', 'true');
      label.appendChild(document.createElement('span')).className = LABEL_CLASS + '__name';
      label.appendChild(document.createElement('span')).className = LABEL_CLASS + '__price';
      inner.appendChild(label);
    }
    // Only ever written when it has actually changed, so this never sets off
    // its own watcher in a loop.
    var name = label.firstChild;
    var price = label.lastChild;
    if (name.textContent !== parts.name) name.textContent = parts.name;
    if (price.textContent !== parts.price) price.textContent = parts.price;
    if (price.hidden !== !parts.price) price.hidden = !parts.price;
    if (!inner.classList.contains(HAS_LABEL_CLASS)) inner.classList.add(HAS_LABEL_CLASS);
  }

  /** Brings one picker's thumbnail and label in line with what is chosen in it. */
  function sync(picker) {
    var inner = directChild(picker, '.tpo_option-input-inner');
    if (!inner) return;

    var field = inner.querySelector('input.tpo_option-dropdown');
    var value = field ? flatten(field.value) : '';
    syncLabel(inner, value);
    if (!value) { clear(inner); return; }

    var src = chosenImage(picker, value);
    if (!src) { clear(inner); return; }

    var thumb = inner.querySelector('.' + THUMB_CLASS);
    if (!thumb) {
      thumb = makeThumb();
      inner.insertBefore(thumb, inner.firstChild);
    }
    // Only ever written when it has actually changed, so this never sets off
    // its own watcher in a loop.
    if (thumb.getAttribute('src') !== src) thumb.setAttribute('src', src);
    if (!inner.classList.contains(HAS_THUMB_CLASS)) inner.classList.add(HAS_THUMB_CLASS);
  }

  function syncAll() {
    var pickers = document.querySelectorAll('.tpo_option-input-wrapper-dropdown');
    for (var i = 0; i < pickers.length; i++) {
      try { sync(pickers[i]); } catch (e) { /* one bad picker never stops the rest */ }
    }
  }

  /* ---- staying in step with Easify ----------------------------------
   * Easify publishes no "selection changed" event to listen for, so this
   * follows the approach already proven elsewhere on this page: watch its
   * own container for changes, and keep a light poll running underneath for
   * anything that changes without touching that subtree.
   * ------------------------------------------------------------------ */

  function start() {
    var root = document.querySelector('.tpo_option-set-container')
      || document.querySelector('.tpo_option-set-wrapper');
    if (!root) return false;

    syncAll();

    var timer = null;
    function soon() {
      clearTimeout(timer);
      timer = setTimeout(syncAll, 80);
    }

    root.addEventListener('change', soon, true);
    root.addEventListener('click', soon, true);

    if (window.MutationObserver) {
      new MutationObserver(soon).observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'value', 'src', 'hidden', 'aria-selected']
      });
    }

    setInterval(syncAll, 900);
    return true;
  }

  /**
   * Easify draws its options after the page itself has loaded, and how long
   * that takes varies, so this waits for the options to appear rather than
   * for any particular moment — and gives up quietly after half a minute on
   * a product that simply has no options.
   */
  function waitForEasify() {
    if (start()) return;
    var tries = 0;
    var poll = setInterval(function () {
      tries++;
      if (start() || tries > 60) clearInterval(poll);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForEasify);
  } else {
    waitForEasify();
  }
})();
